import { Button, Card, EmptyState, Skeleton, formatDate } from "@helmr/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { exportMonthCsv, staffMonth, type AttendanceDay, type StaffMonth } from "../../api/attendance";
import { listStaff } from "../../api/staff";
import { NoAccess } from "../../app/NoAccess";
import { useServices } from "../../app/services";
import { Failure, messageOf, styles as c, useScope } from "../classes/common";
import { cellKind, DayEditDrawer, STATUS_CODE, STATUS_LABEL } from "./attendanceBits";
import { monthDates, monthLabel, shiftMonth } from "./month";
import s from "./staff.module.css";

/** Rows are staff, columns the month's days. `GET /attendance` is one date at a time, so the grid reads each person's month. */
export function AttendanceGrid() {
  const { client, session } = useServices();
  const sc = useScope();
  const [month, setMonth] = useState(sc.date.slice(0, 7));
  const [edit, setEdit] = useState<{ staffId: string; name: string; day: AttendanceDay | null; date: string } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // The grid reads the staff list (GET /staff) and each month (GET /attendance/:staffId): both levers.
  const allowed = sc.ctx.has("staff_attendance.view") && sc.ctx.has("staff.view");
  const people = useQuery({ queryKey: ["staff", ...sc.key, "grid"], queryFn: () => listStaff(client, { status: "active" }).then((r) => r.staff.filter((m) => !sc.branchId || m.homeBranchId === sc.branchId)), enabled: allowed, retry: false });
  const months = useQueries({ queries: (people.data ?? []).map((m) => ({ queryKey: ["attendance", ...sc.key, m.id, month], queryFn: () => staffMonth(client, m.id, month), retry: false })) });
  if (sc.ctx.status !== "loading" && !allowed) return <NoAccess />;
  const dates = monthDates(month);
  const canEdit = sc.ctx.has("staff_attendance.correct");
  const canExport = sc.ctx.has("exports.staff_attendance");
  const failed = months.find((m) => m.error);

  const download = async () => {
    setExporting(true); setExportError(null);
    try {
      const csv = await exportMonthCsv(session.getState().token, { month, branchId: sc.branchId });
      if (typeof URL.createObjectURL === "function") {
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `attendance-${month}.csv`; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { setExportError(messageOf(e)); } finally { setExporting(false); }
  };

  return (
    <div className={c.page}>
      <div className={c.head}>
        <div><h1>Staff attendance</h1><p>{monthLabel(month, sc.ctx.locale)}</p></div>
        <div className={c.tools}>
          <Button size="sm" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>‹</Button>
          <Button size="sm" onClick={() => setMonth(sc.date.slice(0, 7))}>This month</Button>
          <Button size="sm" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}>›</Button>
          <Button as={Link} to="/staff">Staff</Button>
          {canExport ? <Button busy={exporting} busyLabel="Exporting…" onClick={() => void download()}>Export CSV</Button> : null}
        </div>
      </div>
      {exportError ? <p className={c.err} role="alert">{exportError}</p> : null}
      <Card aria-label="Attendance grid">
        {people.isPending ? <div aria-busy="true" aria-label="Loading attendance" className={c.skRow}><Skeleton width="100%" height={34} /><Skeleton width="100%" height={34} /><Skeleton width="100%" height={34} /></div>
          : people.error ? <Failure error={people.error} title="Could not load staff" onRetry={() => void people.refetch()} />
          : people.data.length === 0 ? <EmptyState icon="badge" title="No staff yet" description="Add staff to track their attendance." action={sc.ctx.has("staff.add") ? <Button as={Link} to="/staff/new" variant="primary">Add staff</Button> : undefined} />
          : (
            <>
              {failed ? <Failure error={failed.error} title="Some attendance did not load" onRetry={() => months.forEach((m) => m.error && void m.refetch())} /> : null}
              <div className={s.grid} role="region" aria-label="Attendance by day" tabIndex={0}>
                <table>
                  <thead><tr><th className={s.name} scope="col">Staff</th>{dates.map((d) => <th key={d} scope="col" title={formatDate(d, sc.ctx.locale, "weekday")}>{Number(d.slice(8))}</th>)}</tr></thead>
                  <tbody>
                    {people.data.map((m, i) => {
                      const data: StaffMonth | undefined = months[i]?.data;
                      return (
                        <tr key={m.id}>
                          <td className={s.name}><Link to={`/staff/${m.id}?tab=attendance`} title={m.name}>{m.name}</Link></td>
                          {dates.map((d) => {
                            const day = data?.days.find((x) => x.date === d) ?? null;
                            const kind = cellKind(day);
                            const label = `${m.name}, ${d}: ${day ? STATUS_LABEL[kind] ?? day.status : data ? "no record" : "loading"}`;
                            const text = day ? STATUS_CODE[kind] ?? "?" : "·";
                            return (
                              <td key={d} className={s[`s_${kind}`]}>
                                {canEdit && data ? <button type="button" className={s.cell} title={label} aria-label={label} onClick={() => setEdit({ staffId: m.id, name: m.name, day, date: d })}>{text}</button> : <span className={s.cell} title={label} aria-label={label}>{text}</span>}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className={s.legend} aria-label="Legend">{["present", "late", "half_day", "absent", "leave", "holiday", "off"].map((k) => <span key={k}><b>{STATUS_CODE[k]}</b> {STATUS_LABEL[k]}</span>)}<span><b>·</b> No record</span></div>
            </>
          )}
      </Card>
      {edit ? <DayEditDrawer staffId={edit.staffId} name={edit.name} date={edit.date} current={edit.day} onClose={() => setEdit(null)} /> : null}
    </div>
  );
}
